import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string | undefined
  error?: string | undefined
  hint?: string | undefined
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, type = 'text', disabled, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id || generatedId
    const errorId = `${inputId}-error`
    const hintId = `${inputId}-hint`
    const describedBy = error ? errorId : hint ? hintId : undefined

    return (
      <div className="w-full flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-semibold uppercase tracking-wider text-fg-subtle"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(
            'w-full rounded-sm border bg-surface-1 px-4 py-2.5 text-base sm:text-sm text-fg placeholder:text-fg-muted min-h-[44px]',
            'transition-colors duration-150',
            'border-border focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong',
            error && 'border-error focus:border-error focus:ring-error',
            disabled && 'opacity-50 cursor-not-allowed bg-surface-2',
            className
          )}
          {...props}
        />
        {hint && !error && (
          <span id={hintId} className="text-xs text-fg-muted">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className="text-xs font-medium text-error" role="alert">
            {error}
          </span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
